import { formatUnits, getAddress } from 'viem'
import type { TeamBadge } from 'views/Social/api'
import { ADDRESS_RE } from '../pool-search/logic'

/**
 * Who a wallet is on the competition board. The words come from the CADINU social profile (`GET /api/social/profiles?wallets=`, owned by the social
 * profiles work; the board only READS it):
 *   on   a profile with a handle (and maybe an avatar): the handle and the avatar
 *   off  the user switched the profile off BY CHOICE: no handle and no avatar are ever shown, only the address (muted, tagged "Profile off")
 *   none no profile: only the address
 * A wallet the lookup could not answer for is simply absent: it is shown like `none`, without a label (unknown is not "off").
 * A handle that is only a placeholder ("NFT", "user", ...) counts as no name: nothing extra is written under or beside the address.
 */
export type ProfileState = 'on' | 'off' | 'none'

export interface WalletProfile {
  state: ProfileState
  /** Only when state is 'on' and the profile has a real handle. */
  name?: string
  /** Only when state is 'on' and the profile has an avatar (https URL or a path on this site). */
  avatar?: string
  /** Only when state is 'on': the gold "Official CADINU account" tick (Bez 2026-10-04). */
  official?: true
  /** Only when state is 'on': blue "Token team" ticks, at most two, each checked by isBadge. */
  teams?: TeamBadge[]
}

export interface ProfilesResponse {
  profiles: Record<string, WalletProfile>
  builtAt: number
}

/** One request carries this many wallets: the URL stays far below any proxy's limit (100 x 43 characters). */
export const MAX_WALLETS = 100

/** `a,b,c` to unique lowercase addresses. null for anything else: nothing is guessed, one bad entry refuses the whole request. */
export function parseWallets(raw: unknown): string[] | null {
  if (typeof raw !== 'string' || raw === '') return null
  const parts = raw.split(',').map((s) => s.trim())
  if (parts.length === 0 || parts.length > MAX_WALLETS * 2) return null
  if (!parts.every((p) => ADDRESS_RE.test(p))) return null
  const unique = Array.from(new Set(parts.map((p) => p.toLowerCase())))
  return unique.length <= MAX_WALLETS ? unique : null
}


const PLACEHOLDER = /^(nft|nfts|user|users|player|anon|anonymous|unknown|none|null|undefined|guest|default|name|username|profile)$/i
/** A name nobody chose: the generic words a profile can carry before its owner sets one. */
export const isPlaceholderName = (v: string) => PLACEHOLDER.test(v.trim()) || /^0x[0-9a-f]{4,}/i.test(v.trim())
/** A handle is shown only when it has a sane shape and is not a placeholder; anything else is dropped. */
export const cleanName = (v: unknown): string | undefined =>
  typeof v === 'string' && /^[A-Za-z0-9._-]{1,32}$/.test(v) && !isPlaceholderName(v) ? v : undefined
/** An avatar is an https URL or a path on this site; nothing else is put in an <img>. */
export const cleanAvatar = (v: unknown): string | undefined => (typeof v === 'string' && v.length <= 400 && (/^https:\/\/[^\s"'<>]+$/.test(v) || /^\/(?!\/)[A-Za-z0-9_\-./]+$/.test(v)) ? v : undefined)

/** EIP-55 form of an address; whatever is not an address comes back as it was. */
export function checksum(wallet: string): string {
  try {
    return getAddress(wallet)
  } catch {
    return wallet
  }
}
/** `0x058c…A089`: the checksummed address, first six and last four characters. */
export function shortChecksum(wallet: string): string {
  const a = checksum(wallet)
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a
}

export type ScoreKind = 'VOLUME' | 'PURCHASE_HOLD' | string

export interface ScoreView {
  /** What the row shows: a readable number, compact when large ("50.61M"). Never cut in the middle. */
  figure: string
  /** The unit the rules define: VOLUME is project-token volume; PURCHASE_HOLD is project tokens x seconds held. */
  unit: string
  /** The whole value, for a tooltip and a screen reader. */
  full: string
}

const group = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 })

/**
 * The score as the rules define it. The API sends an integer in the project token's base units (for Buy and hold, multiplied by seconds). With the
 * token's decimals it becomes a number of tokens; without them (the token could not be read) the whole integer is shown, grouped, never shortened.
 */
export function scoreView(raw: string, decimals: number | undefined, symbol: string | undefined, kind: ScoreKind): ScoreView {
  const sym = symbol || 'tokens'
  const unit = kind === 'PURCHASE_HOLD' ? `${sym}·s` : sym
  if (!/^\d+$/.test(raw)) return { figure: raw, unit: '', full: raw }
  if (decimals === undefined || !Number.isInteger(decimals) || decimals < 0 || decimals > 77) {
    const g = group(raw.replace(/^0+(?=\d)/, ''))
    return { figure: g, unit: kind === 'PURCHASE_HOLD' ? 'base units·s' : 'base units', full: g }
  }
  const text = formatUnits(BigInt(raw), decimals) // exact, e.g. "50610000.123456789"
  const [whole, frac = ''] = text.split('.')
  const fullFrac = frac.slice(0, 6).replace(/0+$/, '')
  const full = `${group(whole)}${fullFrac ? `.${fullFrac}` : ''}`
  const n = Number(text)
  let figure: string
  if (n === 0) figure = '0'
  else if (n < 0.000001) figure = '<0.000001'
  else if (n < 1) figure = Number(n.toPrecision(3)).toString()
  else if (n < 1000) figure = n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  else figure = COMPACT.format(n)
  return { figure, unit, full }
}

/* ---------------------------------------------------------------------------------------------------------------------------------------------
 * Prize vs score, said in words (Bez 2026-10-04: "it is not clear which number is the prize and which is the score", "what is 176.94B CADINU·s").
 * The prize is gold and labelled "Est. prize" until the result is final; the score is said the human way for each competition type, with the raw
 * value the rules use kept in the tooltip.
 * ------------------------------------------------------------------------------------------------------------------------------------------- */

/** What the board's prize column is called: an estimate until the result is final on chain. */
export const prizeLabel = (kind?: string | null) => (kind === 'final' ? 'Prize' : 'Est. prize')

/** A span of seconds the way a person says it: "42 min", "5.2 h", "2.0 d". */
export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0 min'
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)} h`
  return `${(seconds / 86_400).toFixed(1)} d`
}

/**
 * The seconds a Buy & hold score covers: from the start to the moment the scorer counted up to. A provisional board is counted up to the time it was
 * published (the scorer's own cursor, a few seconds earlier at most); a final or candidate result, and any board after the end, to the end.
 */
export function holdWindow({ start, end, publishedAt, kind }: { start: number; end: number; publishedAt?: string | null; kind?: string | null }): number {
  if (!(end > start)) return 0
  const at = kind === 'final' || kind === 'candidate' ? end : publishedAt ? Math.floor(Date.parse(publishedAt) / 1000) : NaN
  if (!Number.isFinite(at)) return 0
  return Math.max(0, Math.min(at, end) - start)
}

const compactNumber = (n: number) => {
  if (n === 0) return '0'
  if (n < 0.000001) return '<0.000001'
  if (n < 1) return Number(n.toPrecision(3)).toString()
  if (n < 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  return COMPACT.format(n)
}
/** "$12.3K", "$4.20", "<$0.01". */
export function fmtUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '$0'
  if (n < 0.01) return '<$0.01'
  if (n < 1000) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return `$${COMPACT.format(n)}`
}

export interface ScoreHuman {
  /** the big figure of the score cell: "4.55M CADINU", "Volume $12.3K" */
  main: string
  /** the line under it: "avg held · 1.5 d", "1.23M CBON traded" */
  sub: string
  /** one sentence for the tooltip and screen readers, with the raw score the rules use */
  tip: string
}

/**
 * The score said the human way.
 *   Buy & hold  score = tokens held x seconds held, summed over every purchase. Divided by the seconds the competition has run, it is the average amount
 *               held over the competition so far: "4.55M CADINU", "avg held · 1.5 d". Exact, not an estimate: score / window.
 *   Volume      score = project tokens bought + sold. In dollars at today's price when the price is known ("Volume $12.3K", "1.23M CBON traded"),
 *               otherwise in tokens ("Volume 1.23M CBON").
 * Without the token's decimals (or a window) the rules' own unit is shown, never a guess.
 */
export function scoreHuman(raw: string, decimals: number | undefined, symbol: string | undefined, kind: ScoreKind, opts: { windowSeconds?: number; priceUsd?: number | null } = {}): ScoreHuman {
  const sym = symbol || 'tokens'
  const v = scoreView(raw, decimals, symbol, kind)
  const okDecimals = decimals !== undefined && Number.isInteger(decimals) && decimals >= 0 && decimals <= 77
  if (kind === 'PURCHASE_HOLD') {
    const tip0 = `Score = tokens held × seconds held: ${v.full} ${v.unit}`.trim()
    const w = Math.floor(opts.windowSeconds ?? 0)
    if (!/^\d+$/.test(raw) || !okDecimals || w <= 0) return { main: `${v.figure} ${v.unit}`.trim(), sub: 'tokens × seconds held', tip: tip0 }
    const avgUnits = BigInt(raw) / BigInt(w)
    const avg = Number(formatUnits(avgUnits, decimals as number))
    const avgFull = scoreView(avgUnits.toString(), decimals, symbol, 'VOLUME').full
    const dur = fmtDuration(w)
    return {
      main: `${compactNumber(avg)} ${sym}`,
      sub: `avg held · ${dur}`,
      tip: `${tip0} (${v.figure}). Over the ${dur} counted so far that is ${avgFull} ${sym} held on average.`,
    }
  }
  const tokens = `${v.figure} ${v.unit}`.trim()
  const tip = `Score = ${sym} bought + sold in this pool: ${v.full} ${v.unit}`.trim()
  const price = opts.priceUsd
  if (okDecimals && /^\d+$/.test(raw) && typeof price === 'number' && Number.isFinite(price) && price > 0) {
    const usd = Number(formatUnits(BigInt(raw), decimals as number)) * price
    return { main: `Volume ${fmtUsd(usd)}`, sub: `${tokens} traded`, tip: `${tip}, about ${fmtUsd(usd)} at today's price.` }
  }
  return { main: `Volume ${tokens}`, sub: 'bought + sold', tip }
}

export interface Legend {
  /** what the score is, for this type */
  score: string
  /** what the prize column is */
  prize: string
  /** both, as one sentence pair (for a title or a screen reader) */
  text: string
}
/** The legend under the board's title: how this type scores, and what the prize column is. */
export function scoreLegend(kind: ScoreKind, opts: { symbol?: string; winners?: number; pool?: string; final?: boolean }): Legend {
  const sym = opts.symbol || 'the project token'
  const who = opts.winners ? `the top ${opts.winners}` : 'the winners'
  const pool = opts.pool ? ` ${opts.pool}` : ' the prize pool'
  const prize = opts.final ? `${who} shared${pool} by score.` : `${who} share${pool} by score, if it ended now.`
  const score = kind === 'PURCHASE_HOLD' ? `${sym} bought in this competition × the time you keep it, shown as the average held so far.` : `${sym} bought + sold in this pool.`
  return { score, prize, text: `Score: ${score} ${prizeLabel(opts.final ? 'final' : null)}: ${prize}` }
}
